import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { authService } from '../services/authService';
import { notify } from '../utils/notifications';
import PasswordRequirements from '../components/PasswordRequirements';
import { passwordPolicyError } from '../utils/passwordPolicy';

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit() {
    if (!token) return;
    const passwordError = passwordPolicyError(password);
    if (passwordError) {
      notify.error(passwordError);
      return;
    }
    if (password !== confirmation) {
      notify.error('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      await authService.resetPassword(token, password);
      notify.success('Password updated', 'Sign in with your new password.');
      navigate('/signin', { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      notify.error(
        'Could not reset password',
        message && message !== '[object Object]'
          ? message
          : 'This link may have expired. Request a new one and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="size-full flex flex-col overflow-y-auto px-6" style={{ backgroundColor: 'var(--app-bg)' }}>
      <button type="button" aria-label="Back to sign in" onClick={() => navigate('/signin')} className="mt-6 -ml-2 size-11 rounded-full flex items-center justify-center active:opacity-70" style={{ color: 'var(--app-text)' }}>
        <ArrowLeft className="w-6 h-6" />
      </button>
      <div className="flex-1 flex flex-col justify-center pb-14">
        <div className="rounded-2xl size-16 flex items-center justify-center mb-6 border" style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}>
          <LockKeyhole className="w-8 h-8" style={{ color: 'var(--app-accent)' }} />
        </div>
        <h1 className="text-[30px] tracking-tight" style={{ color: 'var(--app-text)' }}>Choose a new password</h1>
        <p className="text-base mt-3 leading-6" style={{ color: 'var(--app-text-muted)' }}>Use at least 8 characters and a password you have not used before.</p>

        {!token ? (
          <div className="mt-8 rounded-xl p-4 border" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <p style={{ color: 'var(--app-text)' }}>This reset link is incomplete or invalid.</p>
            <button onClick={() => navigate('/forgot-password')} className="mt-5 h-12 w-full rounded-xl text-white" style={{ backgroundColor: 'var(--app-accent-solid)' }}>Request a new link</button>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-4">
            <PasswordInput id="new-password" label="New password" value={password} onChange={setPassword} show={showPassword} onToggle={() => setShowPassword((current) => !current)} />
            <PasswordRequirements password={password} />
            <PasswordInput id="confirm-password" label="Confirm new password" value={confirmation} onChange={setConfirmation} show={showPassword} onToggle={() => setShowPassword((current) => !current)} />
            <button onClick={handleSubmit} disabled={isSubmitting} className="mt-2 h-14 rounded-xl text-white text-lg disabled:opacity-70 active:scale-[0.98]" style={{ backgroundColor: 'var(--app-accent-solid)' }}>
              {isSubmitting ? 'Updating…' : 'Reset password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PasswordInput({ id, label, value, onChange, show, onToggle }: { id: string; label: string; value: string; onChange: (value: string) => void; show: boolean; onToggle: () => void }) {
  return <div className="flex flex-col gap-2"><label htmlFor={id} className="text-base" style={{ color: 'var(--app-text)' }}>{label}</label><div className="flex rounded-xl" style={{ backgroundColor: 'var(--app-input-bg)', border: '1px solid var(--app-input-border)' }}><input id={id} type={show ? 'text' : 'password'} autoComplete="new-password" value={value} onChange={(event) => onChange(event.target.value)} className="h-14 min-w-0 flex-1 bg-transparent px-4 text-base outline-none" style={{ color: 'var(--app-text)' }} /><button type="button" aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`} onClick={onToggle} className="px-4" style={{ color: 'var(--app-text-subtle)' }}>{show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></div>;
}
